'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Settings, ChevronDown, Lock, Cookie } from 'lucide-react';
import Link from 'next/link';
import { cookieManager, CookiePreferences } from '../lib/cookieManager';

export function CookieConsent() {
  const [showConsent, setShowConsent] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    if (!cookieManager.hasConsent()) {
      // Add a slight delay for better UX
      const timer = setTimeout(() => setShowConsent(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load existing preferences
      const existingPrefs = cookieManager.getPreferences();
      if (existingPrefs) {
        setPreferences(existingPrefs);
      }
    }
  }, []);


  const handleRejectAll = () => {
    const rejectedPrefs: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    
    // Use cookie manager to properly handle rejection
    cookieManager.setPreferences(rejectedPrefs);
    setPreferences(rejectedPrefs);
    setShowConsent(false);
  };

  const handleAcceptAll = () => {
    const acceptedPrefs: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    
    // Use cookie manager to properly handle acceptance
    cookieManager.setPreferences(acceptedPrefs);
    setPreferences(acceptedPrefs);
    setShowConsent(false);
  };

  const handleSavePreferences = () => {
    // Use cookie manager to save custom preferences
    cookieManager.setPreferences(preferences);
    setShowConsent(false);
  };

  if (!showConsent) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-start px-4 py-6 pointer-events-none sm:p-6">
      <div className="max-w-md w-full">
        <AnimatePresence>
          {showConsent && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 25 }}
              className="relative bg-white rounded-2xl shadow-xl border border-primary/10 overflow-hidden pointer-events-auto"
            >
              {/* Premium Background */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Base grid pattern */}
                <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(57,255,20,0.02)_1px,transparent_1px),linear-gradient(to_right,rgba(57,255,20,0.02)_1px,transparent_1px)] bg-[size:2rem_2rem] [transform:perspective(1000px)_rotateX(60deg)] opacity-20" />
                
                {/* Gradient orb */}
                <motion.div
                  className="absolute -right-20 -bottom-20 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-primary/3 to-primary-light/3 blur-[100px]"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </div>

              <div className="relative p-8">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center text-primary">
                      <Cookie className="h-6 w-6" />
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <h2 className="text-2xl font-bold text-black">
                      Your Privacy Matters
                    </h2>
                    <p className="mt-2 text-gray-600 leading-relaxed">
                      We use cookies to enhance your experience and analyze our performance. 
                      By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                      <Link href="/legal/privacy" className="text-primary hover:text-primary/80 font-medium transition-colors">
                        Privacy Policy
                      </Link>{' '}
                      and{' '}
                      <Link href="/legal/cookie" className="text-primary hover:text-primary/80 font-medium transition-colors">
                        Cookie Policy
                      </Link>{' '}
                      to learn more.
                    </p>

                    {/* Cookie Preferences Section */}
                    <AnimatePresence>
                      {showPreferences && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-200"
                        >
                          <div className="text-sm text-gray-700 mb-4">
                            Choose which cookies you'd like to allow:
                          </div>
                          
                          <div className="space-y-3">
                            {/* Essential - Always Active */}
                            <div className="flex items-center justify-between py-2">
                              <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-sm font-medium text-gray-700">Essential</span>
                              </div>
                              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Required</span>
                            </div>
                            
                            {/* Analytics Toggle */}
                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm text-gray-700">Analytics</span>
                              <CompactToggle
                                checked={preferences.analytics}
                                onChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                              />
                            </div>
                            
                            {/* Marketing Toggle */}
                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm text-gray-700">Marketing</span>
                              <CompactToggle
                                checked={preferences.marketing}
                                onChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                              />
                            </div>
                            
                            {/* Personalization Toggle */}
                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm text-gray-700">Personalization</span>
                              <CompactToggle
                                checked={preferences.personalization}
                                onChange={(checked) => setPreferences({ ...preferences, personalization: checked })}
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                </div>

                <div className="mt-8 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleRejectAll}
                    className="flex-1 px-4 py-3 rounded-xl text-gray-700 bg-gray-100 font-medium 
                             border border-gray-200 hover:bg-gray-200 hover:border-gray-300 
                             transition-all duration-200"
                  >
                    Reject All
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowPreferences(!showPreferences)}
                    className="px-4 py-3 rounded-xl text-gray-700 bg-white font-medium 
                             border border-gray-300 hover:bg-gray-50 hover:border-gray-400 
                             transition-all duration-200"
                  >
                    {showPreferences ? 'Hide' : 'Customize'}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-3 rounded-xl text-white bg-[#4CAF50] font-medium 
                             border border-[#4CAF50] hover:bg-[#45a049] hover:border-[#45a049] 
                             transition-all duration-200"
                  >
                    Accept All
                  </motion.button>
                </div>
                
                {/* Save Preferences Button - Only show when preferences are expanded */}
                <AnimatePresence>
                  {showPreferences && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-4"
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleSavePreferences}
                        className="w-full px-4 py-3 rounded-xl text-white bg-primary font-medium 
                                 hover:bg-primary/90 transition-all duration-200"
                      >
                        Save My Preferences
                      </motion.button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


interface CompactToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function CompactToggle({ checked, onChange }: CompactToggleProps) {
  return (
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="w-10 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer 
                  peer-checked:after:translate-x-4 peer-checked:after:border-white 
                  after:content-[''] after:absolute after:top-[2px] after:left-[2px] 
                  after:bg-white after:border-gray-300 after:border after:rounded-full 
                  after:h-5 after:w-5 after:transition-all peer-checked:bg-primary">
      </div>
    </label>
  );
}
